import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { isTeacherSession } from '../http/offline-preload.service';
import { SyncService } from '../sync/sync.service';

/**
 * The sign-in loading page.
 *
 * A full page in the teacher app's own look - its header, background, cards and
 * blue - rather than a dialog over a dimmed screen. It is what the teacher sees
 * between signing in and their dashboard while their classes, assessments and
 * every assessment screen are saved to this device.
 *
 * Shown for teacher sessions only; other roles never use these screens, and
 * their load runs silently. Stages report failure individually and the load
 * carries on, because partial data beats none.
 */
@Component({
  selector: 'app-hydration-overlay',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (visible()) {
      <section
        class="prep"
        role="status"
        aria-live="polite"
        [class.prep--done]="sync.hydration().finished"
      >
        <header class="prep-header">
          <img
            class="prep-logo"
            src="assets/img/samLogoWhite.png"
            alt="SAM"
          />
        </header>

        <div class="prep-body">
          <div class="prep-content">
            <div class="prep-mark" [class.prep-mark--done]="sync.hydration().finished">
              @if (sync.hydration().finished) {
                <svg viewBox="0 0 24 24" class="prep-tick" aria-hidden="true">
                  <path
                    d="M4 12.5 9.5 18 20 7"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              } @else {
                <span class="prep-ring" aria-hidden="true"></span>
              }
            </div>

            <h1 class="prep-title">{{ title() }}</h1>
            <p class="prep-subtitle">{{ subtitle() }}</p>

            <div class="prep-progress">
              <div class="prep-progress-row">
                <span>{{ progressLabel() }}</span>
                <span class="prep-percent">{{ sync.hydrationPercent() }}%</span>
              </div>
              <div
                class="prep-bar"
                role="progressbar"
                [attr.aria-valuenow]="sync.hydrationPercent()"
                aria-valuemin="0"
                aria-valuemax="100"
              >
                <div class="prep-bar-fill" [style.width.%]="sync.hydrationPercent()"></div>
              </div>
            </div>

            <ol class="prep-stages">
              @for (stage of sync.hydration().stages; track stage.key) {
                <li class="prep-stage" [attr.data-status]="stage.status">
                  <span class="prep-stage-icon" aria-hidden="true">
                    @switch (stage.status) {
                      @case ('done') {
                        <svg viewBox="0 0 16 16" class="prep-stage-tick">
                          <path
                            d="M3 8.5 6.5 12 13 4.5"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                        </svg>
                      }
                      @case ('failed') {
                        <span class="prep-stage-warn">!</span>
                      }
                      @case ('loading') {
                        <span class="prep-stage-spinner"></span>
                      }
                      @default {
                        <span class="prep-stage-idle"></span>
                      }
                    }
                  </span>

                  <span class="prep-stage-label">{{ label(stage) }}</span>

                  @if (stage.status === 'failed') {
                    <span class="prep-stage-count prep-stage-count--warn">{{ text.retryLater }}</span>
                  } @else if (stage.records > 0) {
                    <span class="prep-stage-count">{{ stage.records }}</span>
                  }
                </li>
              }
            </ol>

            @if (sync.hydration().error && sync.hydration().finished) {
              <p class="prep-warning">{{ text.partialLoad }}</p>
            } @else if (!sync.hydration().finished) {
              <p class="prep-note">{{ tr('offline.loadingNote', text.loadingNote) }}</p>
            }
          </div>
        </div>
      </section>
    }
  `,
  styles: [
    `
      .prep {
        position: fixed;
        inset: 0;
        z-index: 2000;
        display: flex;
        flex-direction: column;
        overflow-y: auto;
        background: var(--teacher-sidebar-color, #F5F7FA);
        color: var(--color-text-main, #0f172a);
        font-family: var(--teacher-font, 'Noto Sans'), system-ui, -apple-system, sans-serif;
        animation: prep-fade-in 0.2s ease-out;
      }
      .prep--done {
        animation: prep-fade-out 0.9s ease-in forwards;
      }

      /* The teacher app's header bar. */
      .prep-header {
        flex: 0 0 auto;
        display: flex;
        align-items: center;
        height: 64px;
        padding: 0 1.5rem;
        background: var(--sam-teacher-primary, #3F5480);
        box-shadow: 0 2px 4px rgba(0,0,0,0.1);
      }
      .prep-logo {
        height: 2.25rem;
        width: auto;
      }

      .prep-body {
        flex: 1 0 auto;
        display: flex;
        justify-content: center;
        padding: 2.5rem 1.25rem 3rem;
      }
      .prep-content {
        width: min(38rem, 100%);
        background: var(--sam-admin-card-bg, #ffffff);
        padding: 2rem;
        border-radius: var(--sam-teacher-card-radius, 1rem);
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
      }

      .prep-mark {
        display: grid;
        place-items: center;
        width: 4rem;
        height: 4rem;
        margin-bottom: 1.25rem;
        border-radius: 50%;
        background: var(--sam-teacher-primary-light, #ebf0fc);
        color: var(--sam-teacher-primary, #3f5480);
      }
      .prep-mark--done {
        background: #dcfce7;
        color: var(--status-completed-text, #16a34a);
      }
      .prep-ring {
        width: 1.9rem;
        height: 1.9rem;
        border-radius: 50%;
        border: 3px solid rgba(63, 84, 128, 0.2);
        border-top-color: currentColor;
        animation: prep-spin 0.8s linear infinite;
      }
      .prep-tick {
        width: 2.2rem;
        height: 2.2rem;
        animation: prep-pop 0.35s cubic-bezier(0.2, 1.4, 0.4, 1);
      }

      .prep-title {
        margin: 0 0 0.4rem;
        font-size: 1.5rem;
        font-weight: 700;
        color: var(--sam-teacher-dark, #3a4e88);
      }
      .prep-subtitle {
        margin: 0 0 1.75rem;
        font-size: 0.95rem;
        line-height: 1.5;
        color: var(--color-text-secondary, #475569);
      }

      .prep-progress {
        margin-bottom: 1.5rem;
      }
      .prep-progress-row {
        display: flex;
        justify-content: space-between;
        margin-bottom: 0.5rem;
        font-size: 0.85rem;
        color: var(--color-text-secondary, #475569);
      }
      .prep-percent {
        font-weight: 600;
        font-variant-numeric: tabular-nums;
        color: var(--sam-teacher-primary, #3f5480);
      }
      .prep-bar {
        height: 0.5rem;
        border-radius: 999px;
        background: var(--color-border, #e2e8f0);
        overflow: hidden;
      }
      .prep-bar-fill {
        height: 100%;
        border-radius: inherit;
        background: var(--sam-teacher-primary, #3f5480);
        transition: width 0.45s cubic-bezier(0.4, 0, 0.2, 1);
      }

      .prep-stages {
        list-style: none;
        margin: 0;
        padding: 0;
        display: grid;
        gap: 0.75rem;
      }
      .prep-stage {
        display: flex;
        align-items: center;
        gap: 0.85rem;
        padding: 0.95rem 1.1rem;
        background: var(--color-surface, #ffffff);
        border: 1px solid var(--color-border, #e2e8f0);
        border-radius: var(--sam-teacher-card-radius, 1rem);
        font-size: 0.925rem;
        color: var(--color-text-muted, #94a3b8);
        transition: border-color 0.25s ease, color 0.25s ease, box-shadow 0.25s ease;
      }
      .prep-stage[data-status='loading'] {
        color: var(--color-text-main, #0f172a);
        border-color: var(--sam-teacher-primary, #3f5480);
        box-shadow: 0 0 0 3px var(--sam-teacher-primary-light, #ebf0fc);
      }
      .prep-stage[data-status='done'] {
        color: var(--color-text-main, #0f172a);
      }
      .prep-stage[data-status='failed'] {
        color: #b45309;
      }

      .prep-stage-icon {
        flex: 0 0 1.25rem;
        height: 1.25rem;
        display: grid;
        place-items: center;
      }
      .prep-stage-idle {
        width: 0.55rem;
        height: 0.55rem;
        border-radius: 50%;
        background: var(--status-pending-border, #cbd5e1);
      }
      .prep-stage-spinner {
        width: 1.05rem;
        height: 1.05rem;
        border-radius: 50%;
        border: 2px solid var(--sam-teacher-primary-light, #ebf0fc);
        border-top-color: var(--sam-teacher-primary, #3f5480);
        animation: prep-spin 0.7s linear infinite;
      }
      .prep-stage-tick {
        width: 1.1rem;
        height: 1.1rem;
        color: var(--status-completed-text, #16a34a);
        animation: prep-pop 0.3s cubic-bezier(0.2, 1.4, 0.4, 1);
      }
      .prep-stage-warn {
        font-weight: 700;
        color: #b45309;
      }
      .prep-stage-label {
        flex: 1;
      }
      .prep-stage-count {
        font-size: 0.825rem;
        font-variant-numeric: tabular-nums;
        color: var(--color-text-secondary, #475569);
      }
      .prep-stage-count--warn {
        color: #b45309;
      }

      .prep-note,
      .prep-warning {
        margin: 1.5rem 0 0;
        font-size: 0.85rem;
        line-height: 1.5;
        color: var(--color-text-secondary, #475569);
      }
      .prep-warning {
        color: #b45309;
      }

      @media (max-width: 640px) {
        .prep-header {
          padding: 0 1rem;
        }
        .prep-body {
          padding: 1.75rem 1rem 2.5rem;
        }
        .prep-content {
          padding: 1.5rem;
        }
        .prep-title {
          font-size: 1.25rem;
        }
      }

      @keyframes prep-spin {
        to {
          transform: rotate(360deg);
        }
      }
      @keyframes prep-pop {
        from {
          transform: scale(0.4);
          opacity: 0;
        }
        to {
          transform: scale(1);
          opacity: 1;
        }
      }
      @keyframes prep-fade-in {
        from {
          opacity: 0;
        }
      }
      @keyframes prep-fade-out {
        to {
          opacity: 0;
        }
      }

      /* Keep the information, drop the movement. */
      @media (prefers-reduced-motion: reduce) {
        .prep,
        .prep--done,
        .prep-ring,
        .prep-tick,
        .prep-stage-spinner,
        .prep-stage-tick,
        .prep-bar-fill {
          animation: none !important;
          transition: none !important;
        }
      }
    `,
  ],
})
export class HydrationOverlayComponent {
  protected readonly sync = inject(SyncService);
  private readonly translate = inject(TranslateService);

  /**
   * Teachers only. Read when the load starts or changes, which is after login
   * has stored the session's access level.
   */
  protected readonly visible = computed(
    () => this.sync.hydration().active && isTeacherSession(),
  );

  protected readonly progressLabel = computed(() => {
    const h = this.sync.hydration();
    return h.finished
      ? this.tr('offline.progressDone', this.text.progressDone)
      : `${this.tr('offline.progressStep', this.text.progressStep)} ${Math.min(
          h.completedStages + 1,
          h.totalStages,
        )} / ${h.totalStages}`;
  });

  /**
   * English copy, used directly.
   *
   * Not routed through the translate pipe, and that is deliberate. This app's
   * MissingTranslationHandler returns an **empty string** for any dotted key it
   * cannot resolve, so an untranslated page would render blank rows - a loading
   * screen with no words on it, which looks far more broken than one in the
   * wrong language. `tr()` opts each string back into translation the moment a
   * real key exists.
   */
  protected readonly text = {
    loadingTitle: 'Getting your assessments ready',
    readyTitle: 'All set',
    loadingSubtitle:
      'We are saving your classes, students and assessments to this device, so every screen opens instantly and keeps working without internet.',
    readySubtitle:
      'Everything is saved on this device. Taking you to your dashboard.',
    loadingNote:
      'This happens when you sign in. Please keep the app open until it finishes.',
    progressStep: 'Step',
    progressDone: 'Complete',
    retryLater: 'will retry',
    partialLoad:
      'Some data could not be saved yet. It will sync automatically in the background.',
  };

  protected title(): string {
    return this.sync.hydration().finished
      ? this.tr('offline.readyTitle', this.text.readyTitle)
      : this.tr('offline.loadingTitle', this.text.loadingTitle);
  }

  protected subtitle(): string {
    return this.sync.hydration().finished
      ? this.tr('offline.readySubtitle', this.text.readySubtitle)
      : this.tr('offline.loadingSubtitle', this.text.loadingSubtitle);
  }

  protected label(stage: { labelKey: string; label: string }): string {
    return this.tr(stage.labelKey, stage.label);
  }

  /**
   * Translate if the key resolves to real text, otherwise use the English.
   *
   * Guards against both failure modes: the handler's empty string, and
   * ngx-translate echoing the key back when no handler applies.
   */
  protected tr(key: string, fallback: string): string {
    const value = this.translate.instant(key);
    return typeof value === 'string' && value.trim() !== '' && value !== key
      ? value
      : fallback;
  }
}
